import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { AreaRef, Role, StaffDetail as StaffDetailType } from '@foundation/shared';
import { ROLES, ROLE_LABEL_TH } from '@foundation/shared';
import { api } from '../lib/api';
import { STAFF_STATUS_TH, formatDate, formatDateTime, formatNumber } from '../lib/format';
import { Badge, ErrorAlert, Loading, Modal } from '../components/ui';
import { useAuth } from '../auth';

export default function StaffDetail() {
  const { id = '' } = useParams();
  const { me, allowed } = useAuth();
  const [staff, setStaff] = useState<StaffDetailType | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [invite, setInvite] = useState<{ url: string; expiresAt: string } | null>(null);
  const [editingAreas, setEditingAreas] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setStaff(await api<StaffDetailType>(`/staff/${id}`));
    } catch (e) {
      setError(e);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(fn: () => Promise<void>, message: string) {
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(message);
      await load();
    } catch (e) {
      setError(e);
    }
  }

  if (error && !staff) return <ErrorAlert error={error} />;
  if (!staff) return <Loading />;

  const isSelf = me?.id === staff.id;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{staff.fullName}</h1>
          <p>
            <span className="mono">{staff.staffCode}</span> · {ROLE_LABEL_TH[staff.role]} ·{' '}
            <Badge value={staff.status} label={STAFF_STATUS_TH[staff.status] ?? staff.status} />
          </p>
        </div>
        <Link className="btn secondary" to="/staff">
          ← กลับรายการ
        </Link>
      </div>

      <ErrorAlert error={error} />
      {notice && <div className="alert success">{notice}</div>}

      {invite && (
        <div className="alert warn">
          <strong>ลิงก์ผูกบัญชี LINE (แสดงครั้งเดียว)</strong>
          <p className="small" style={{ margin: '6px 0' }}>
            ส่งลิงก์นี้ให้เจ้าหน้าที่เปิดในแอป LINE — หมดอายุ {formatDateTime(invite.expiresAt)}
          </p>
          <div className="mono" style={{ wordBreak: 'break-all', marginBottom: 8 }}>
            {invite.url}
          </div>
          <div className="btn-row">
            <button
              type="button"
              className="secondary"
              onClick={() => void navigator.clipboard.writeText(invite.url)}
            >
              คัดลอกลิงก์
            </button>
            <button type="button" className="link" onClick={() => setInvite(null)}>
              ปิด
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-2">
        <div className="card">
          <h2>ข้อมูลทั่วไป</h2>
          <dl className="detail-list">
            <dt>ชื่อเล่น</dt>
            <dd>{staff.nickname ?? '—'}</dd>
            <dt>เบอร์โทร</dt>
            <dd>{staff.phone ?? '—'}</dd>
            <dt>อีเมลติดต่อ</dt>
            <dd>{staff.email ?? '—'}</dd>
            <dt>เข้าเว็บด้วย Google</dt>
            <dd>{staff.googleEmail ?? <span className="muted">ไม่ได้ให้สิทธิ์</span>}</dd>
            <dt>ทีม</dt>
            <dd>{staff.teamName ?? '—'}</dd>
            <dt>เริ่มงาน</dt>
            <dd>{formatDate(staff.employedAt)}</dd>
            <dt>บัญชี LINE</dt>
            <dd>
              {staff.lineLinked ? (
                <span className="badge green">ผูกแล้ว</span>
              ) : (
                <span className="badge amber">ยังไม่ผูก</span>
              )}
            </dd>
            <dt>เข้าใช้ล่าสุด</dt>
            <dd>{formatDateTime(staff.lastLoginAt)}</dd>
          </dl>
        </div>

        <div className="card">
          <h2>ภาระงาน</h2>
          {staff.workload ? (
            <dl className="detail-list">
              <dt>ผู้รับความช่วยเหลือที่ดูแล</dt>
              <dd>{formatNumber(staff.workload.activeBeneficiaries)} ราย</dd>
              <dt>นัดเยี่ยมค้าง</dt>
              <dd>{formatNumber(staff.workload.pendingVisits)} รายการ</dd>
              <dt>เลยกำหนด</dt>
              <dd>
                {staff.workload.overdueVisits > 0 ? (
                  <span className="badge red">{formatNumber(staff.workload.overdueVisits)} รายการ</span>
                ) : (
                  '0 รายการ'
                )}
              </dd>
              <dt>เคสที่เปิดอยู่</dt>
              <dd>{formatNumber(staff.workload.openCases)} เคส</dd>
            </dl>
          ) : (
            <p className="muted small">ยังไม่มีข้อมูลภาระงาน (เจ้าหน้าที่ยังไม่ปฏิบัติงาน)</p>
          )}
        </div>
      </div>

      <div className="card">
        <h2>พื้นที่รับผิดชอบ</h2>
        <p className="muted small" style={{ marginTop: 0 }}>
          กำหนดขอบเขตข้อมูลที่เจ้าหน้าที่ภาคสนามมองเห็น — เห็นเฉพาะเคสของตัวเอง
          และเคสที่ยังไม่มีผู้ดูแลในพื้นที่เหล่านี้
        </p>
        {staff.areas.length === 0 ? (
          <p className="muted">ยังไม่ได้กำหนดพื้นที่</p>
        ) : (
          <div className="btn-row" style={{ marginBottom: 12 }}>
            {staff.areas.map((a) => (
              <span key={a.id} className="badge blue">
                {a.nameTh}
              </span>
            ))}
          </div>
        )}
        {allowed('staff:write') && (
          <button className="secondary" onClick={() => setEditingAreas(true)}>
            แก้ไขพื้นที่รับผิดชอบ
          </button>
        )}
      </div>

      {allowed('staff:write') && (
        <div className="card">
          <h2>การจัดการบัญชี</h2>
          <div className="btn-row">
            {!staff.lineLinked && staff.status !== 'resigned' && (
              <button
                onClick={() =>
                  void act(async () => {
                    const res = await api<{ url: string; expiresAt: string }>(
                      `/staff/${staff.id}/invite`,
                      { method: 'POST' },
                    );
                    setInvite(res);
                  }, 'สร้างลิงก์ผูกบัญชีเรียบร้อย')
                }
              >
                สร้างลิงก์ผูกบัญชี LINE
              </button>
            )}

            {me?.role === 'super_admin' && staff.lineLinked && (
              <button
                className="secondary"
                onClick={() => {
                  if (!confirm(`ปลดการผูก LINE ของ ${staff.fullName}?\nเจ้าหน้าที่จะใช้งานผ่าน LINE ไม่ได้จนกว่าจะผูกใหม่`))
                    return;
                  void act(
                    () => api(`/staff/${staff.id}/line`, { method: 'DELETE' }),
                    'ปลดการผูกบัญชี LINE แล้ว',
                  );
                }}
              >
                ปลดการผูก LINE
              </button>
            )}

            {!isSelf && staff.status === 'active' && (
              <button
                className="secondary"
                onClick={() => {
                  if (!confirm(`ระงับการใช้งานของ ${staff.fullName} ชั่วคราว?`)) return;
                  void act(
                    () =>
                      api(`/staff/${staff.id}/status`, {
                        method: 'PATCH',
                        body: { status: 'suspended' },
                      }),
                    'ระงับการใช้งานแล้ว — เซสชันทั้งหมดถูกเพิกถอนทันที',
                  );
                }}
              >
                ระงับชั่วคราว
              </button>
            )}

            {!isSelf && staff.status === 'suspended' && (
              <button
                onClick={() =>
                  void act(
                    () =>
                      api(`/staff/${staff.id}/status`, {
                        method: 'PATCH',
                        body: { status: 'active' },
                      }),
                    'เปิดใช้งานอีกครั้งแล้ว',
                  )
                }
              >
                เปิดใช้งานอีกครั้ง
              </button>
            )}

            {!isSelf && staff.status !== 'resigned' && (
              <button
                className="danger"
                onClick={() => {
                  const reason = prompt('เหตุผลการสิ้นสุดการทำงาน');
                  if (!reason) return;
                  void act(
                    () =>
                      api(`/staff/${staff.id}/status`, {
                        method: 'PATCH',
                        body: { status: 'resigned', reason },
                      }),
                    'บันทึกการสิ้นสุดการทำงานแล้ว',
                  );
                }}
              >
                สิ้นสุดการทำงาน
              </button>
            )}
          </div>
          {isSelf && (
            <p className="muted small" style={{ marginBottom: 0 }}>
              ไม่สามารถเปลี่ยนสิทธิ์หรือสถานะของตัวเองได้
            </p>
          )}
        </div>
      )}

      {me?.role === 'super_admin' && !isSelf && (
        <div className="card">
          <h2>สิทธิ์การใช้งาน</h2>
          <div className="field" style={{ maxWidth: 320 }}>
            <label htmlFor="roleSelect">สิทธิ์ปัจจุบัน</label>
            <select
              id="roleSelect"
              value={staff.role}
              onChange={(e) => {
                const next = e.target.value as Role;
                if (!confirm(`เปลี่ยนสิทธิ์เป็น "${ROLE_LABEL_TH[next]}"?`)) return;
                void act(
                  () => api(`/staff/${staff.id}`, { method: 'PATCH', body: { role: next } }),
                  'เปลี่ยนสิทธิ์เรียบร้อย — มีผลทันทีทุกอุปกรณ์',
                );
              }}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL_TH[r]}
                </option>
              ))}
            </select>
            <div className="hint">การเปลี่ยนสิทธิ์ถูกบันทึกลง audit log ทุกครั้ง</div>
          </div>
        </div>
      )}

      {editingAreas && (
        <AreaPicker
          selected={staff.areaIds}
          onClose={() => setEditingAreas(false)}
          onSave={(areaIds) =>
            void act(async () => {
              await api(`/staff/${staff.id}`, { method: 'PATCH', body: { areaIds } });
              setEditingAreas(false);
            }, 'บันทึกพื้นที่รับผิดชอบแล้ว')
          }
        />
      )}
    </>
  );
}

function AreaPicker({
  selected,
  onClose,
  onSave,
}: {
  selected: string[];
  onClose: () => void;
  onSave: (areaIds: string[]) => void;
}) {
  const [areas, setAreas] = useState<AreaRef[] | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set(selected));
  const [filter, setFilter] = useState('');

  useEffect(() => {
    api<AreaRef[]>('/meta/areas')
      .then(setAreas)
      .catch(() => setAreas([]));
  }, []);

  const visible = (areas ?? []).filter(
    (a) => !filter || a.nameTh.includes(filter) || a.code.includes(filter),
  );

  return (
    <Modal title="เลือกพื้นที่รับผิดชอบ" onClose={onClose}>
      <div className="field">
        <label htmlFor="areaFilter">ค้นหาพื้นที่</label>
        <input
          id="areaFilter"
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="ชื่อตำบล/อำเภอ หรือรหัส"
        />
      </div>

      {areas === null ? (
        <Loading />
      ) : areas.length === 0 ? (
        <div className="alert info small">
          ยังไม่มีข้อมูลพื้นที่ในระบบ — นำเข้าข้อมูลตำบล/อำเภอลงตาราง <code>area</code> ก่อน
        </div>
      ) : (
        <div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 14 }}>
          {visible.map((a) => (
            <label key={a.id} className="checkbox" style={{ padding: '4px 0' }}>
              <input
                type="checkbox"
                checked={chosen.has(a.id)}
                onChange={(e) => {
                  const next = new Set(chosen);
                  if (e.target.checked) next.add(a.id);
                  else next.delete(a.id);
                  setChosen(next);
                }}
              />
              <span>
                {a.nameTh} <span className="muted small mono">{a.code}</span>
              </span>
            </label>
          ))}
        </div>
      )}

      <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="secondary" onClick={onClose}>
          ยกเลิก
        </button>
        <button type="button" onClick={() => onSave([...chosen])}>
          บันทึก ({chosen.size} พื้นที่)
        </button>
      </div>
    </Modal>
  );
}
